import { useState, useEffect, useMemo } from "react";
import { Tabs, Tab, styled, CircularProgress } from "@mui/material";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import { useNavigate, useLocation } from "react-router-dom";
import MasterDataTable, { MasterType } from "./components/MasterDataTable";
import actionsHandler from "@/store/actions/actionHandler";
import { useDispatch, useSelector } from "react-redux";
import { AppThunkDispatch, RootState } from "@/store";
import { EndpointType } from "@/constants/api";
import { usePermissionState } from "@/providers/PermissionProvider";
import {
  BasePermissions,
  ClientTypesPermissions,
  CommodityPermissions,
  DebitorsPermissions,
  PaymentTermsPermissions,
  ProfessionsPermissions,
} from "@/constants/permission-modules/base-permissions";

const StyledTabs = styled(Tabs)({
  minHeight: "unset",
  "& .MuiTabs-indicator": {
    display: "none",
  },
});

const StyledTab = styled(Tab)({
  minHeight: "unset",
  padding: "8px 16px",
  textTransform: "none",
  fontSize: "14px",
  fontWeight: 600,
  minWidth: "unset",
});

interface Tab {
  label: string;
  path: string;
  type: MasterType;
  permission?: BasePermissions;
}

const tabsArray: Tab[] = [
  {
    label: "Commodity",
    path: "/general-masters/commodity",
    type: "commodity",
    permission: CommodityPermissions.VIEW_COMMODITY,
  },
  {
    label: "Profession",
    path: "/general-masters/profession",
    type: "profession",
    permission: ProfessionsPermissions.VIEW_PROFESSIONS,
  },
  {
    label: "Debtor Type",
    path: "/general-masters/debtor-type",
    type: "debtorType",
    permission: DebitorsPermissions.VIEW_DEBTOR_TYPE,
  },
  {
    label: "Payment Terms",
    path: "/general-masters/payment-terms",
    type: "paymentTerms",
    permission: PaymentTermsPermissions.VIEW_PAYMENT_TERMS,
  },
  {
    label: "Client Type",
    path: "/general-masters/client-type",
    type: "clientType",
    permission: ClientTypesPermissions.VIEW_CLIENT_TYPES,
  },
  {
    label: "Bank",
    path: "/general-masters/bank",
    type: "bank",
  },
];

interface FetchParams {
  limit?: number;
  page?: number;
  search?: string;
  sortColumn?: string;
  sortDirection?: "asc" | "desc";
}

const GeneralMastersLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch<AppThunkDispatch>();
  const { isPermitted } = usePermissionState();
  const user = useSelector((state: RootState) => state.auth.user);

  const tabs = useMemo(() => {
    return tabsArray.filter((tab) =>
      tab.permission ? isPermitted(tab.permission) : true,
    );
  }, [user]);

  useEffect(() => {
    setLoading(true);
    const currentPath = location.pathname;
    const tabIndex = tabs.findIndex((tab) => tab.path === currentPath);
    if (tabIndex !== -1 && tabIndex !== activeTab) {
      setActiveTab(tabIndex);
    }
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, [location]);

  useEffect(() => {
    if (tabs.length > 0) {
      handleTabChange(null, 0);
    }
  }, [tabs]);

  const fetchMasterData = async (
    type: MasterType,
    params: FetchParams = {},
    isArchiveView: boolean = false,
  ) => {
    try {
      const { getAllData } = actionsHandler(type as EndpointType);
      const customQuery = `is_archive=${isArchiveView}`;

      // Build ordering string
      let ordering = "";
      if (params.sortColumn) {
        ordering =
          params.sortDirection === "desc"
            ? `-${params.sortColumn}`
            : params.sortColumn;
      }

      const response = await dispatch(
        getAllData({
          limit: params.limit || 10,
          page: params.page || 1,
          search: params.search || "",
          customQuery: `${customQuery}${ordering ? `&ordering=${ordering}` : ""}`,
        }),
      );

      return response;
    } catch (error) {
      console.error(`Error fetching ${type} data:`, error);
      return {};
    }
  };

  const handleTabChange = (
    event: React.SyntheticEvent | null,
    newValue: number,
  ) => {
    setActiveTab(newValue);
    navigate(tabs[newValue].path);
  };

  // Keep your existing JSX until the main content section

  return (
    <div className="tw-relative">
      {loading ? (
        <div className="tw-flex tw-justify-center tw-items-center tw-h-screen">
          <CircularProgress />
        </div>
      ) : (
        <>
          <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
            <div className="tw-px-6 tw-pt-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
              <div className="tw-flex tw-justify-between tw-items-center">
                <div className="tw-flex tw-items-center">
                  <img
                    src={BackIcon}
                    alt="Back Icon"
                    onClick={() => navigate("/menu")}
                    className="tw-cursor-pointer"
                  />
                  <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                    System Configurations
                  </h1>
                </div>
              </div>
              <div className="tw-w-fit tw-mt-4 tw-border tw-border-[#E9EAEB]">
                <StyledTabs value={activeTab} onChange={handleTabChange}>
                  {tabs.map((tab, index) => (
                    <StyledTab
                      key={tab.label}
                      label={tab.label}
                      className={`tw-text-sm tw-font-semibold ${
                        activeTab === index ? "active-tab" : "tw-text-[#232323]"
                      } tab-border`}
                    />
                  ))}
                </StyledTabs>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="tw-mt-[177px]">
            {activeTab !== null && (
              <MasterDataTable
                type={tabs[activeTab].type}
                fetchData={fetchMasterData}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default GeneralMastersLayout;
